import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useRef, useEffect, useCallback } from 'react';
import { useOverlayPosition, useButton, useFocusWithin, FocusScope } from 'react-aria';
import { ButtonUpIcon, ButtonDownIcon } from '@manulife/mux-cds-icons';
import PropTypes from '../../../../node_modules/prop-types/index.js';
import { SVG_ICON_SIZE_DEFAULT } from '../../Icon/constants.js';
import { BREAKPOINTS } from '../../../spacing/index.js';
import { safeRest, rem, noop } from '../../../utils/functions.js';
import handle from '../../../utils/handle.js';
import { HeaderButtonComponent, IconWrapper, LabelWrapper, MenuList } from './styledComponents.js';
import useTheme from '../../../hooks/useTheme.js';
import useMedia from '../../../hooks/useMedia.js';
import useToggle from '../../../hooks/useToggle.js';

var _excluded = ["id", "label", "hideLabel", "icon", "showMenuArrow", "onClick", "children"];
var HeaderButton = function HeaderButton(_ref) {
  var id = _ref.id,
    label = _ref.label,
    hideLabel = _ref.hideLabel,
    icon = _ref.icon,
    defaultShowMenuArrow = _ref.showMenuArrow,
    onClick = _ref.onClick,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var showHamburgerMenu = useMedia(["(max-width: ".concat(BREAKPOINTS.MAX_1024, "px)")],
  // media queries
  [true],
  // values corresponding to media query
  false // default value
  );

  var _useToggle = useToggle(false),
    _useToggle2 = _slicedToArray(_useToggle, 3),
    isOpen = _useToggle2[0],
    toggleOpen = _useToggle2[1],
    setOpen = _useToggle2[2];
  var triggerRef = useRef(null);
  var overlayRef = useRef(null);
  useEffect(function () {
    if (isOpen) {
      overlayRef.current.focus({
        preventScroll: true
      });
    }
  }, [isOpen]);
  var handleClick = useCallback(function (event) {
    var _triggerRef$current;
    if (children) {
      toggleOpen();
    }
    handle(onClick)(event, (_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.value);
  }, [children, onClick, toggleOpen]);
  function handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  }

  // position props relative to the trigger
  var _useOverlayPosition = useOverlayPosition({
      placement: theme.utilityHeader.menu.direction,
      targetRef: triggerRef,
      containerPadding: 0,
      overlayRef: overlayRef,
      isOpen: isOpen
    }),
    overlayPositionProps = _useOverlayPosition.overlayProps;

  // ensure focus is restored to the button once the menu closes
  var _useButton = useButton({
      onPress: handleClick
    }, triggerRef),
    buttonProps = _useButton.buttonProps;
  var menuTriggerProps = children ? {
    'aria-haspopup': true,
    'aria-expanded': isOpen
  } : {};
  var menuOverlayProps = showHamburgerMenu ? {} : overlayPositionProps;
  var showMenuArrow = children && !showHamburgerMenu && defaultShowMenuArrow;
  var _useFocusWithin = useFocusWithin({
      onBlurWithin: function onBlurWithin() {
        return setOpen(false);
      }
    }),
    focusWithinProps = _useFocusWithin.focusWithinProps;
  var headerButton = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderButtonComponent, _extends({}, buttonProps, menuTriggerProps, {
    id: id,
    theme: theme,
    ref: triggerRef,
    hasIcon: !!icon
  }, safeProps), icon && /*#__PURE__*/React.createElement(IconWrapper, null, /*#__PURE__*/React.cloneElement(icon, {
    fontSize: rem(SVG_ICON_SIZE_DEFAULT)
  })), !hideLabel && label && /*#__PURE__*/React.createElement(LabelWrapper, {
    hasIcon: !!icon
  }, label), showMenuArrow && (isOpen ? /*#__PURE__*/React.createElement(ButtonUpIcon, {
    style: {
      marginLeft: '4px'
    },
    fontSize: rem(SVG_ICON_SIZE_DEFAULT),
    "data-testid": "icon-up"
  }) : /*#__PURE__*/React.createElement(ButtonDownIcon, {
    style: {
      marginLeft: '4px'
    },
    fontSize: rem(SVG_ICON_SIZE_DEFAULT),
    "data-testid": "icon-down"
  }))), isOpen && /*#__PURE__*/React.createElement(FocusScope, {
    restoreFocus: true
  }, /*#__PURE__*/React.createElement(MenuList, _extends({}, menuOverlayProps, {
    ref: overlayRef,
    role: "menu",
    tabIndex: "-1",
    theme: theme,
    onClick: function onClick() {
      return setOpen(false);
    },
    onKeyDown: handleKeyDown
  }), children)));
  return children ? /*#__PURE__*/React.createElement("div", focusWithinProps, headerButton) : headerButton;
};
process.env.NODE_ENV !== "production" ? HeaderButton.propTypes = {
  /** id for the DOM element */
  id: PropTypes.string,
  /** label displayed on header button */
  label: PropTypes.node,
  /** flag to hide label */
  hideLabel: PropTypes.bool,
  /** icon for header button */
  icon: PropTypes.node,
  /** flag to show arrow icon for header button with children (in desktop view only) */
  showMenuArrow: PropTypes.bool,
  /** onClick handler for header button */
  onClick: PropTypes.func
} : void 0;
HeaderButton.defaultProps = {
  id: undefined,
  label: undefined,
  hideLabel: false,
  icon: undefined,
  showMenuArrow: true,
  onClick: noop
};
process.env.NODE_ENV !== "production" ? HeaderButton.displayName = 'HeaderButton' : void 0;
var HeaderButton$1 = HeaderButton;

export { HeaderButton$1 as default };
